import React, { useState, useEffect } from "react";
import { RenderLineNode, DefaultNode } from "./util";
import { Modal, ConfigProvider, Button, message, Upload, List, Space, Typography, Spin } from 'antd';
import { UploadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { getFilesInControlNodeAPI, uploadFileInControlNodeAPI, deleteFileInControlNodeAPI } from '@/services/apis/dashboard';
import type { UploadProps } from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';

const { Title, Text } = Typography;
const { Dragger } = Upload;

interface DDSFileProps {
    node?: DefaultNode;
    visible: boolean;
    onClose: () => void;
}

const Index: React.FC<DDSFileProps> = ({ node, visible, onClose }) => {
    const [modalApi, modalContextHolder] = Modal.useModal();
    const [messageApi, contextHolder] = message.useMessage();
    const [files, setFiles] = useState<API.DDSFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // 加载文件列表
    const loadFiles = async () => {
        if (!node?.ID) return;
        
        setLoading(true);
        try {
            const response = await getFilesInControlNodeAPI(node.ID);
            setFiles(response || []);
        } catch (error) {
            messageApi.error('加载文件列表失败');
            console.error('加载文件列表失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 当组件可见且node存在时加载文件
    useEffect(() => {
        if (visible && node?.ID) {
            loadFiles();
        }
    }, [visible, node?.ID]);

    // 处理文件上传
    const handleUpload: UploadProps['onChange'] = ({ file }) => {
        if (file.status === 'done') {
            messageApi.success(`${file.name} 文件上传成功`);
            // 重新加载文件列表
            loadFiles();
        } else if (file.status === 'error') {
            messageApi.error(`${file.name} 文件上传失败`);
        }
    };

    // 自定义上传方法
    const customRequest = async ({ file, onSuccess, onError }: any) => {
        if (!node?.ID) {
            onError(new Error('节点信息不存在'));
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file as RcFile);

        try {
            await uploadFileInControlNodeAPI(node.ID, formData);
            onSuccess(null, file);
        } catch (error) {
            onError(error);
        } finally {
            setUploading(false);
        }
    };

    // 处理文件删除
    const handleDelete = (fileId: number) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个文件吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    await deleteFileInControlNodeAPI(fileId);
                    messageApi.success('文件删除成功');
                    // 从列表中移除删除的文件
                    setFiles(files.filter(file => file.ID !== fileId));
                } catch (error) {
                    messageApi.error('文件删除失败');
                    console.error('删除文件失败:', error);
                }
            }
        });
    };

    // 预览文件
    const handlePreview = (url: string) => {
        if(!url) {
            messageApi.warning('文件路径不存在');
            return;
        }
        window.open(url, '_blank');
    };

    // 上传配置
    const uploadProps: UploadProps = {
        customRequest,
        onChange: handleUpload,
        multiple: false,
        showUploadList: false,
        beforeUpload: () => {
            if (uploading) {
                messageApi.warning('正在上传中，请稍候再试');
                return Upload.LIST_IGNORE;
            }
            return true;
        }
    };

    return (
        <ConfigProvider
            theme={{
                token: {
                    fontSize: 12
                },
            }}
        >
            {modalContextHolder}
            {contextHolder}
            <Modal 
                open={visible}
                footer={null}
                destroyOnHidden={true}
                onCancel={onClose}
                width="fit-content"
                style={{ minWidth: 520 }}
                title={`${node?.nodeLabel} - 文件管理`}
            >
                <div className="p-4">
                    {/* 文件上传区域 */}
                    <Title level={5}>上传文件</Title>
                    <Dragger {...uploadProps}>
                        <p className="ant-upload-drag-icon">
                            {uploading ? <Spin /> : <UploadOutlined />}
                        </p>
                        <p className="ant-upload-text">
                            {uploading ? '文件上传中，请稍候...' : '点击或拖拽文件到此区域上传'}
                        </p>
                        <p className="ant-upload-hint">
                            {uploading ? '正在处理，请不要关闭对话框' : '支持单个文件上传，文件大小不限'}
                        </p>
                    </Dragger>

                    {/* 文件列表 */}
                    <div className="mt-6">
                        <Title level={5}>文件列表</Title>
                        {loading ? (
                            <div className="text-center py-10">
                                <Spin tip="加载中..." />
                            </div>
                        ) : files.length === 0 ? (
                            <div className="text-center py-10 text-gray-500">
                                暂无文件
                            </div>
                        ) : (
                            <List
                                itemLayout="horizontal"
                                dataSource={files}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <Button
                                                key="preview"
                                                type="link"
                                                icon={<EyeOutlined />}
                                                onClick={() => handlePreview(item.ddsFilePath)}
                                            >
                                                查看
                                            </Button>,
                                            <Button
                                                key="delete"
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleDelete(item.ID)}
                                            >
                                                删除
                                            </Button>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={
                                                <a href={item.ddsFilePath} target="_blank" rel="noopener noreferrer">
                                                    {item.filename}
                                                </a>
                                            }
                                            description={
                                                <Space direction="vertical" size={0}>
                                                    <Text type="secondary">
                                                        上传时间: {new Date(item.CreatedAt).toLocaleString('zh-CN')}
                                                    </Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </div>
                </div>
            </Modal>
        </ConfigProvider>
    );
};

export default Index